import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import { scCatchServiceException } from "@/annotations";
import {
  AccountOption,
  ActivateSocialProfileRequest,
  CreateSocialProfileRequest,
  Page,
  ReviewPrefs,
  SocialNetworkProfile,
  SocialNetworkReview,
  SocialNetworkReviewReply,
  TiktokPreferences,
  UpdateTiktokPreferencesRequest,
  ZillowLenderImportSession,
} from "./dtos";

const BEDROCK = `${Constants.API_URL}/bedrock`;

export class SocialNetworkProfileServices {
  @scCatchServiceException()
  static async getProfiles(userId?: string | number): Promise<Page<SocialNetworkProfile>> {
    const params = userId ? { user_id: userId, page: 0, perPage: 20 } : {};
    const { data } = await axios.get<Page<SocialNetworkProfile>>(`${BEDROCK}/users/${userId}/social_network_profiles`, {
      params,
    });
    return data;
  }

  @scCatchServiceException()
  static async getProfile(userId: string | number, socialProfileId: string | number): Promise<SocialNetworkProfile> {
    const { data } = await axios.get<SocialNetworkProfile>(
      `${BEDROCK}/users/${userId}/social_network_profiles/${socialProfileId}`
    );
    return data;
  }

  @scCatchServiceException()
  static async deleteProfile(userId: string | number, socialProfileId: string | number): Promise<void> {
    await axios.delete(`${BEDROCK}/users/${userId}/social_network_profiles/${socialProfileId}`);
  }

  @scCatchServiceException()
  static async syncProfile(userId: string | number, socialProfileId: string | number): Promise<void> {
    await axios.post(`${BEDROCK}/users/${userId}/social_network_profiles/${socialProfileId}/sync`);
  }

  @scCatchServiceException()
  static async createProfile(
    userId: string | number,
    payload: CreateSocialProfileRequest
  ): Promise<SocialNetworkProfile> {
    const { data } = await axios.post<SocialNetworkProfile>(
      `${BEDROCK}/users/${userId}/social_network_profiles`,
      payload
    );
    return data;
  }

  @scCatchServiceException()
  static async activateProfile(
    userId: string | number,
    socialProfileId: string | number,
    payload: ActivateSocialProfileRequest
  ): Promise<SocialNetworkProfile> {
    const { data } = await axios.post<SocialNetworkProfile>(
      `${BEDROCK}/users/${userId}/social_network_profiles/${socialProfileId}/activate`,
      payload
    );
    return data;
  }

  @scCatchServiceException()
  static async getAccountOptions(userId: string | number, socialProfileId: string | number): Promise<AccountOption[]> {
    const { data } = await axios.get<AccountOption[]>(
      `${BEDROCK}/users/${userId}/social_network_profiles/${socialProfileId}/account_options`
    );
    return data;
  }

  @scCatchServiceException()
  static async getReviews(
    userId: string | number,
    socialIdentityId: string | number,
    page: number = 1,
    perPage: number = 20
  ): Promise<Page<SocialNetworkReview>> {
    const { data } = await axios.get<Page<SocialNetworkReview>>(
      `${BEDROCK}/users/${userId}/social_identities/${socialIdentityId}/reviews`,
      {
        params: {
          page,
          perPage,
          extraFields: ["reply_count"],
        },
      }
    );
    return data;
  }

  @scCatchServiceException()
  static async getReviewReplies(
    userId: string | number,
    socialIdentityId: string | number,
    reviewId: string | number
  ): Promise<SocialNetworkReviewReply[]> {
    const { data } = await axios.get<SocialNetworkReviewReply[]>(
      `${BEDROCK}/users/${userId}/social_identities/${socialIdentityId}/reviews/${reviewId}/review_replies`
    );
    return data;
  }

  @scCatchServiceException()
  static async createReviewReply(
    userId: string | number,
    socialIdentityId: string | number,
    reviewId: string | number,
    message: string
  ): Promise<void> {
    await axios.post(
      `${BEDROCK}/users/${userId}/social_identities/${socialIdentityId}/reviews/${reviewId}/review_replies`,
      { message }
    );
  }

  @scCatchServiceException()
  static async createZillowLenderImportSession(userId: string | number): Promise<ZillowLenderImportSession> {
    const { data } = await axios.post<ZillowLenderImportSession>(`${BEDROCK}/users/${userId}/zillow_lender_reviews`);
    return data;
  }

  @scCatchServiceException()
  static async getTiktokPrefs(userId: string | number): Promise<TiktokPreferences> {
    const { data } = await axios.get<TiktokPreferences>(`${BEDROCK}/users/${userId}/tiktok_preferences/`);
    return data;
  }

  @scCatchServiceException()
  static async updateTiktokPrefs(
    userId: string | number,
    request: UpdateTiktokPreferencesRequest
  ): Promise<TiktokPreferences> {
    const { data } = await axios.patch<TiktokPreferences>(`${BEDROCK}/users/${userId}/tiktok_preferences/`, request);
    return data;
  }

  @scCatchServiceException()
  static async updateReviewSettings(
    userId: string | number,
    socialProfileId: string | number,
    payload: ReviewPrefs
  ): Promise<void> {
    await axios.patch<SocialNetworkProfile>(
      `${BEDROCK}/users/${userId}/social_network_profiles/${socialProfileId}/update_review_settings`,
      payload
    );
  }
}
